import {
  GridFilterOperator,
  GridFilterItem,
  getGridDateOperators,
  GridFilterInputValueProps,
} from "@mui/x-data-grid";
import { Box, Typography, TextField } from "@mui/material";
import { useState } from "react";

// Custom input component for the "between" filter
function DateRangeInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;
  const [startDate, setStartDate] = useState<string>(item.value?.[0] || "");
  const [endDate, setEndDate] = useState<string>(item.value?.[1] || "");

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = event.target.value;
    setStartDate(newStartDate);
    applyValue({
      ...item,
      value: [newStartDate, endDate],
    });
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = event.target.value;
    setEndDate(newEndDate);
    applyValue({
      ...item,
      value: [startDate, newEndDate],
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      {/* Title Above Date Selectors */}
      <Typography variant="body2" sx={{ fontWeight: "medium", fontSize: "0.875rem" }}>
        Date Range (Inclusive)
      </Typography>

      {/* Date Range Selectors */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5, // Increase spacing between elements
          width: "100%", // Ensure the container takes full width
          "& .MuiTextField-root": {
            flex: 1, // Make text fields grow proportionally
            minWidth: "180px", // Ensure adequate minimum width for each text field
          },
        }}
      >
        <TextField
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          size="small"
        />
        <Typography
          variant="body2"
          sx={{ whiteSpace: "nowrap", fontSize: "0.875rem" }}
        >
          to
        </Typography>
        <TextField
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          size="small"
        />
      </Box>
    </Box>
  );
}

// Custom date filter operators
export const dateOperators: GridFilterOperator[] = [
  ...getGridDateOperators(), // Include default date operators
  {
    label: "between",
    value: "between",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.value ||
        !Array.isArray(filterItem.value) ||
        filterItem.value.length !== 2
      ) {
        return null;
      }

      const [startDate, endDate] = filterItem.value.map((date) => new Date(date));
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return null;
      }

      return ({ value }) => {
        if (!value) return false;
        const cellValue = new Date(value);
        if (isNaN(cellValue.getTime())) return false;
        return cellValue >= startDate && cellValue <= endDate;
      };
    },
    InputComponent: DateRangeInput,
  },
];
